import { useMemo, useState } from "react";
import { categories, hotFeed, hotRanking } from "@/mocks/home";

export default function LiveFeedSection() {
  const [active, setActive] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const keyword = query.trim();
    return hotFeed.filter((item) => {
      const matchCategory = active === "all" || item.categoryId === active;
      const matchKeyword =
        keyword === "" ||
        item.title.includes(keyword) ||
        item.summary.includes(keyword) ||
        item.category.includes(keyword);
      return matchCategory && matchKeyword;
    });
  }, [active, query]);

  const tabs = [{ id: "all", name: "全部" }, ...categories.map((c) => ({ id: c.id, name: c.name }))];

  return (
    <section id="live" className="bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="reveal max-w-3xl">
          <span className="text-xs font-label font-medium tracking-widest text-primary-600">
            LIVE FRONTLINE
          </span>
          <h2 className="mt-3 font-heading text-3xl md:text-4xl font-black text-foreground-950">
            实时前线 · 热点信息流
          </h2>
          <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
            按主题筛选或直接搜索关键词，最新进度会持续补位到列表顶部。
          </p>
        </div>

        <div className="reveal mt-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActive(tab.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  active === tab.id
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72 shrink-0">
            <i
              className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"
              aria-hidden="true"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索热点关键词"
              aria-label="搜索热点关键词"
              className="w-full pl-10 pr-4 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
            />
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-8">
            {filtered.length === 0 ? (
              <div className="rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 px-6 text-center">
                <i className="ri-inbox-line text-4xl text-foreground-300" aria-hidden="true" />
                <p className="mt-4 text-sm text-foreground-600">
                  没有找到匹配「{query}」的热点，换个关键词或切回全部板块试试。
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setActive("all");
                  }}
                  className="mt-5 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-600"
                >
                  <i className="ri-refresh-line" aria-hidden="true" />
                  重置筛选
                </button>
              </div>
            ) : (
              <ol className="space-y-3">
                {filtered.map((item, index) => (
                  <li key={item.id}>
                    <article className="group rounded-lg bg-background-100 border border-background-200 p-4 md:p-5 transition-colors hover:border-primary-300 hover:bg-background-50">
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5">
                        <span className="inline-flex items-center gap-1.5 text-xs font-label font-medium text-primary-600">
                          {item.isLive && (
                            <span className="w-1.5 h-1.5 rounded-full bg-primary-500 pulse-dot" aria-hidden="true" />
                          )}
                          {item.category}
                        </span>
                        <span className="text-xs text-foreground-400">{item.time}</span>
                        <span className="inline-flex items-center gap-1 text-xs text-secondary-700">
                          <i className="ri-fire-line" aria-hidden="true" />
                          {item.heat}
                        </span>
                      </div>

                      <h3 className="mt-2.5 font-heading text-lg md:text-xl font-bold leading-snug text-foreground-950">
                        <a href="#deep" className="cursor-pointer transition-colors hover:text-primary-600">
                          {item.title}
                        </a>
                      </h3>

                      <p className="mt-2 text-sm leading-relaxed text-foreground-600 line-clamp-2">
                        {item.summary}
                      </p>

                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs font-label text-foreground-400">
                          序号 {String(index + 1).padStart(2, "0")}
                        </span>
                        <a
                          href="#deep"
                          className="inline-flex items-center gap-1 text-xs font-medium text-foreground-700 transition-colors group-hover:text-primary-600 cursor-pointer"
                        >
                          查看展开报道
                          <i className="ri-arrow-right-line" aria-hidden="true" />
                        </a>
                      </div>
                    </article>
                  </li>
                ))}
              </ol>
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-6">
              <div className="rounded-lg bg-foreground-950 text-background-100 p-5 md:p-6">
                <h3 className="font-heading text-lg font-bold text-background-50 flex items-center gap-2">
                  <i className="ri-fire-line text-secondary-400" aria-hidden="true" />
                  热搜榜
                </h3>
                <ol className="mt-5 space-y-3.5">
                  {hotRanking.map((item, index) => (
                    <li key={item.id} className="flex items-center gap-3">
                      <span
                        className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-heading font-black shrink-0 ${
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100/10 text-background-100/70"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <a
                        href="#live"
                        className="flex-1 text-sm text-background-100/90 truncate cursor-pointer transition-colors hover:text-secondary-300"
                      >
                        {item.label}
                      </a>
                      <span className="flex items-center gap-1 text-xs font-label text-background-100/60 whitespace-nowrap">
                        {item.trend === "up" && <i className="ri-arrow-up-line text-primary-400" aria-hidden="true" />}
                        {item.trend === "down" && <i className="ri-arrow-down-line text-accent-400" aria-hidden="true" />}
                        {item.trend === "flat" && <i className="ri-subtract-line" aria-hidden="true" />}
                        {item.heat}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg bg-secondary-100 border border-secondary-200 p-5 md:p-6">
                <h3 className="font-heading text-base font-bold text-secondary-950 flex items-center gap-2">
                  <i className="ri-mail-send-line" aria-hidden="true" />
                  有料想爆料？
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-secondary-900/80">
                  把你掌握的时间、地点与佐证材料发到编辑邮箱，核实后我们会第一时间跟进。
                </p>
                <a
                  href="mailto:contact@chiguakanliao.com"
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-secondary-500 text-secondary-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-secondary-600"
                >
                  发送线索
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}